
<template>
  <div :class="[
    `${prefix}input-group`,
    { ...getInputGroupContainerClasses }
  ]">
    <div
      v-if="label"
      :class="`${prefix}input-group__label`"
    >
      <mpx-label :label="label" />
      <template v-if="tooltipContent">
        <mpx-tooltip
          trigger="hover"
          placement="top"
          :content="tooltipContent"
          :target="tooltipId"
        />
        <mpx-icon
          variant="info-small"
          size="small"
          :id="tooltipId"
        />
      </template>
      <span
        v-if="showCounter"
        :class="`${prefix}input-group__counter`"
      >
        <span>{{ inputCount }}</span>
        <span
          v-if="inputLimit"
          ref="input-limit"
        >/ {{ inputLimit }}</span>
      </span>
    </div>
    <span
      v-if="prepend"
      :class="`${prefix}input--prepend`"
    >
      <slot name="prepend">
        {{ prepend }}
      </slot>
    </span>
    <span
      v-if="append && !(!!iconAppend)"
      :class="`${prefix}input--append`"
    >
      <slot name="append">
        {{ append }}
      </slot>
    </span>
    <span
      v-if="iconPrepend"
      :class="[
        `${prefix}input__glyph--prepend`,
        { ...getGlyphClasses }
      ]"
      tabindex="0"
      @click="$emit('icon-prepend-click', $event)"
      @blur="$emit('icon-prepend-blur', $event)"
    >
      <slot name="iconPrepend">
        <mpx-icon
          :variant="iconPrepend"
          size="small"
          primary-color="dark"
        />
      </slot>
    </span>
    <span
      v-if="iconAppend && !(!!append)"
      :class="[
        `${prefix}input__glyph--append`,
        { ...getGlyphClasses }
      ]"
      tabindex="0"
      @click="$emit('icon-append-click', $event)"
      @blur="$emit('icon-append-blur', $event)"
    >
      <slot name="iconAppend">
        <mpx-icon
          :variant="iconAppend"
          size="small"
        />
      </slot>
    </span>
    <span
      v-if="hasResetIcon"
      v-show="isOnHover"
      :class="[
        `${prefix}input__glyph--append`,
        `${prefix}input__glyph--append-reset-icon`,
        { ...getResetIconClasses }
      ]"
      @mouseenter="isOnHover = true"
      @mouseleave="isOnHover = false"
      @click="() => {
        localValue = null;
        $emit('reset-input');
      }"
    >
      <mpx-icon
        variant="reset"
        size="small"
        primary-color="muted"
      />
    </span>
    <slot
      :setLocalValue="(value) => {
        localValue = value;
      }"
      :setOnHover="(value) => {
        isOnHover = value;
      }"
      :emitOnBlur="(e) => $emit('blur', e)"
      :emitOnFocus="(e) => $emit('focus', e)"
    />
    <slot name="errorMessage">
      <small
        v-if="errorMessage"
        :class="`${prefix}w-full ${prefix}error--message`"
      >
        {{ errorMessage }}
      </small>
    </slot>
    <slot name="caption">
      <small
        v-if="caption"
        ref="input-group-caption"
        :class="`${prefix}w-full ${prefix}mt-1`"
      >
        {{ caption }}
      </small>
    </slot>
  </div>
</template>

<script>
import MpxIcon from '../Icon';
import MpxLabel from '../Label';
import MpxTooltip from '../Tooltip';

export default {
  name: 'MpxInputGroup',
  components: {
    MpxIcon,
    MpxLabel,
    MpxTooltip,
  },
  props: {
    prefix: {
      type: String,
      default: 'mpx-',
    },
    value: {
      type: [String, Number],
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    prepend: {
      type: String,
      default: null,
    },
    append: {
      type: String,
      default: null,
    },
    iconPrepend: {
      type: String,
      default: null,
    },
    iconAppend: {
      type: String,
      default: null,
    },
    hasResetIcon: {
      type: Boolean,
      default: false,
    },
    tooltipContent: {
      type: String,
      default: null,
    },
    tooltipId: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: null,
    },
    variant: {
      type: String,
      default: 'text-field',
    },
    errorMessage: {
      type: String,
      default: null,
    },
    caption: {
      type: String,
      default: null,
    },
    showCounter: {
      type: Boolean,
      default: false,
    },
    clickableIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localValue: null,
      inputCount: 0,
      isOnHover: false,
    };
  },
  watch: {
    value(newValue) {
      this.localValue = newValue;
    },
    localValue(newValue) {
      this.inputCount = newValue ? newValue.length : 0;
      this.$emit('input', newValue);
    },
  },
  computed: {
    inputLimit() {
      return this.$attrs.maxLength;
    },
    isSmall() {
      return this.size === 'small';
    },
    getInputGroupContainerClasses() {
      const classes = {};
      classes[`${this.prefix}input-group--small`] = this.isSmall;
      return classes;
    },
    getResetIconClasses() {
      const classes = {};
      classes[`${this.prefix}input__glyph--append-reset-icon-only`] = !(this.append) && !(this.iconAppend);
      return classes;
    },
    getGlyphClasses() {
      const classes = {};
      classes[`${this.prefix}cursor-pointer`] = this.clickableIcon;
      return classes;
    },
  },
  mounted() {
    this.localValue = this.value;
  },
};
</script>
